/**
 * Модальное окно на базе Radix UI Dialog в виде workspace-dialog макета-эталона:
 * подложка, шапка с заголовком и кнопкой закрытия, тело. Закрытие возвращает фокус
 * элементу, с которого окно открыли: Radix без DialogTrigger фокус не возвращает. Окно
 * рисуется в body: открытое поверх другого окна (подтверждение) позиционируется от экрана,
 * а Escape закрывает только верхнее окно.
 */

import * as DialogPrimitive from '@radix-ui/react-dialog';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { WorkspaceIcon } from '@/components/workspace/WorkspaceIcon';
import { useReturnFocus } from './useReturnFocus';

interface DialogProps {
	/** Управление видимостью */
	open: boolean;
	/** Колбэк при изменении видимости */
	onOpenChange: (open: boolean) => void;
	/** Заголовок модального окна */
	title?: string;
	/** Пояснение под шапкой (notice-note макета) — доступное описание окна */
	description?: string;
	/** Содержимое модального окна */
	children: ReactNode;
	/** Широкое окно — для документа, например предпросмотра PDF */
	wide?: boolean;
	/** Короткое появление и закрытие; учитывает уменьшение движения в системе. */
	animated?: boolean;
}

const WIDE_STYLE = { width: 'min(1040px, calc(100vw - 32px))' } as const;

export function Dialog({
	open,
	onOpenChange,
	title,
	description,
	children,
	wide = false,
	animated = false,
}: DialogProps) {
	const { t } = useTranslation('common');
	const returnFocus = useReturnFocus();

	return (
		<DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
			<DialogPrimitive.Portal>
				{/* Подложка: workspace-dialog::backdrop макета */}
				<DialogPrimitive.Overlay
					data-motion={animated || undefined}
					className="ws-workspace-dialog-backdrop fixed inset-0 z-[var(--z-modal)]"
				/>
				{/* Окно: workspace-dialog макета */}
				<DialogPrimitive.Content
					{...(!description ? { 'aria-describedby': undefined } : {})}
					{...returnFocus}
					aria-modal="true"
					data-motion={animated || undefined}
					className="ws-workspace-dialog fixed top-1/2 left-1/2 z-[var(--z-modal)] -translate-x-1/2 -translate-y-1/2 overflow-auto bg-[var(--ws-surface)] outline-none"
					style={wide ? WIDE_STYLE : undefined}
				>
					<div className="ws-workspace-dialog-head">
						{title && (
							<DialogPrimitive.Title asChild>
								<h2>{title}</h2>
							</DialogPrimitive.Title>
						)}
						<DialogPrimitive.Close
							aria-label={t('actions.close')}
							className={['ws-icon-button', !title ? 'ml-auto' : ''].filter(Boolean).join(' ')}
						>
							<WorkspaceIcon name="close" />
						</DialogPrimitive.Close>
					</div>
					<div className="ws-workspace-dialog-body">
						{description && (
							<DialogPrimitive.Description className="ws-notice-note">
								{description}
							</DialogPrimitive.Description>
						)}
						{children}
					</div>
				</DialogPrimitive.Content>
			</DialogPrimitive.Portal>
		</DialogPrimitive.Root>
	);
}
